import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/ui/stat-card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { Users, MessageSquare, Clock, TrendingUp, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { analyticsService } from "@/services/analyticsService";
import { useAuth } from "@/contexts/AuthContext";

interface AgentTrackTabProps {
  agent: any;
}

interface AgentAnalytics {
  leadsCollected: number;
  assistedCustomers: number;
  timeSaved: number;
  messages: number;
  total: number;
  last30Days: number;
  last7Days: number;
  today: number;
}

interface ConversationVolumeData {
  date: string;
  conversations: number;
  messages: number;
}

const AgentTrackTab: React.FC<AgentTrackTabProps> = ({ agent }) => {
  const navigate = useNavigate();
  const { organization } = useAuth();
  const [analytics, setAnalytics] = useState<AgentAnalytics>({
    leadsCollected: 0,
    assistedCustomers: 0,
    timeSaved: 0,
    messages: 0,
    total: 0,
    last30Days: 0,
    last7Days: 0,
    today: 0,
  });
  const [chartData, setChartData] = useState<ConversationVolumeData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeFrame, setTimeFrame] = useState("30d");

  // Fetch agent-specific analytics using the optimized service
  useEffect(() => {
    const fetchAgentAnalytics = async () => {
      if (!agent?.id || !organization?.id) return;

      setIsLoading(true);
      try {
        // Use the new optimized analytics service
        const { data: analyticsData, error } = await analyticsService.getAgentAnalytics(
          agent.id,
          timeFrame as "all" | "30d"
        );

        if (error) {
          console.error("Error fetching agent analytics:", error);
          return;
        }

        // Fetch additional time-based conversation counts for the chart stats
        const now = new Date();
        const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Fetch time-based conversation counts (external only)
        const [totalResult, last30DaysResult, last7DaysResult, todayResult] = await Promise.all([
          supabase
            .from("conversations")
            .select("*", { count: "exact", head: true })
            .eq("agent_id", agent.id)
            .not("agent_chat_user_id", "is", null),
          supabase
            .from("conversations")
            .select("*", { count: "exact", head: true })
            .eq("agent_id", agent.id)
            .not("agent_chat_user_id", "is", null)
            .gte("created_at", last30Days.toISOString()),
          supabase
            .from("conversations")
            .select("*", { count: "exact", head: true })
            .eq("agent_id", agent.id)
            .not("agent_chat_user_id", "is", null)
            .gte("created_at", last7Days.toISOString()),
          supabase
            .from("conversations")
            .select("*", { count: "exact", head: true })
            .eq("agent_id", agent.id)
            .not("agent_chat_user_id", "is", null)
            .gte("created_at", today.toISOString())
        ]);

        setAnalytics({
          ...analyticsData,
          total: totalResult.count || 0,
          last30Days: last30DaysResult.count || 0,
          last7Days: last7DaysResult.count || 0,
          today: todayResult.count || 0,
        });

        // Fetch chart data for the last 30 days
        await fetchChartData();
      } catch (error) {
        console.error("Error fetching agent analytics:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgentAnalytics();
  }, [agent?.id, organization?.id, timeFrame]);

  const fetchChartData = async () => {
    if (!agent?.id) return;

    try {
      // Generate data for the last 4 months
      const fourMonthsAgo = new Date();
      fourMonthsAgo.setMonth(fourMonthsAgo.getMonth() - 4);

      const { data: conversationsData } = await supabase
        .from("conversations")
        .select("created_at")
        .eq("agent_id", agent.id)
        .not("agent_chat_user_id", "is", null) // Only external conversations
        .gte("created_at", fourMonthsAgo.toISOString())
        .order("created_at", { ascending: true });

      // Group conversations by week (first day of the week)
      const groupedData = conversationsData?.reduce((acc, conv) => {
        const date = new Date(conv.created_at);
        // Get the first day of the week (Monday)
        const firstDayOfWeek = new Date(date);
        const day = firstDayOfWeek.getDay();
        const diff = firstDayOfWeek.getDate() - day + (day === 0 ? -6 : 1);
        firstDayOfWeek.setDate(diff);
        
        const weekKey = firstDayOfWeek.toISOString().split('T')[0];
        acc[weekKey] = (acc[weekKey] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      // Generate chart data for all weeks in the last 6 months
      const chartData: ConversationVolumeData[] = [];
      const today = new Date();
      
      for (let i = 0; i < 18; i++) { // ~18 weeks in 4 months
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - (i * 7));
        
        // Get Monday of this week
        const day = weekStart.getDay();
        const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
        weekStart.setDate(diff);
        
        const weekKey = weekStart.toISOString().split('T')[0];
        const formattedDate = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        
        chartData.unshift({ // Add to beginning for chronological order
          date: formattedDate,
          conversations: groupedData[weekKey] || 0,
          messages: (groupedData[weekKey] || 0) * 3, // Rough estimate: 3 messages per conversation
        });
      }

      setChartData(chartData);
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  };

  const handleViewConversations = () => {
    navigate(`/inbox?agent=${agent.id}`);
  };

  const chartConfig = {
    conversations: {
      label: "Conversations",
      color: "#8aa3d9",
    },
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8aa3d9] mx-auto mb-4"></div>
          <p className="text-gray-500">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Analytics</h1>
            <p className="text-gray-600">Track the performance of your AI agent</p>
          </div>
          <Button onClick={handleViewConversations} className="bg-primary hover:bg-primary/90 text-white">
            View conversations
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Stat Cards Row */}
        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Users}
            iconColor="text-brand-blue"
            label="Leads collected"
            value={analytics.leadsCollected}
          />
          <StatCard
            icon={Users}
            iconColor="text-brand-green"
            label="Assisted Customers"
            value={analytics.assistedCustomers}
          />
          <StatCard
            icon={Clock}
            iconColor="text-brand-purple"
            label="Time Saved"
            value={`${analytics.timeSaved}h`}
          />
          <StatCard
            icon={MessageSquare}
            iconColor="text-brand-red"
            label="Messages"
            value={analytics.messages}
          />
        </div>

        {/* Conversations Volume Chart */}
        <Card className="overflow-hidden border border-gray-100 shadow-sm bg-white">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#8aa3d9]" />
                <h3 className="text-lg font-semibold text-gray-900">Conversations Volume</h3>
              </div>
              
              {/* Small stat cards in top right */}
              <div className="flex gap-2">
                <div className="text-center p-2 bg-gray-50 rounded-lg min-w-[60px]">
                  <div className="text-xs text-gray-500 mb-1">Total</div>
                  <div className="text-sm font-semibold text-gray-900">{analytics.total}</div>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg min-w-[60px]">
                  <div className="text-xs text-gray-500 mb-1">30d</div>
                  <div className="text-sm font-semibold text-gray-900">{analytics.last30Days}</div>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg min-w-[60px]">
                  <div className="text-xs text-gray-500 mb-1">7d</div>
                  <div className="text-sm font-semibold text-gray-900">{analytics.last7Days}</div>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg min-w-[60px]">
                  <div className="text-xs text-gray-500 mb-1">Today</div>
                  <div className="text-sm font-semibold text-gray-900">{analytics.today}</div>
                </div>
              </div>
            </div>
            
            <div className="w-full">
              <ChartContainer config={chartConfig} className="h-80 w-full">
                <BarChart
                  accessibilityLayer
                  data={chartData}
                  margin={{
                    top: 20,
                    left: 12,
                    right: 12,
                  }}
                >
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                  />
                  <ChartTooltip
                    cursor={false}
                    content={<ChartTooltipContent hideLabel />}
                  />
                  <Bar 
                    dataKey="conversations" 
                    fill="var(--color-conversations)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ChartContainer>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AgentTrackTab; 